import { useAtomValue } from 'jotai/utils';
import { addonsSettingsAtom } from 'src/shared/settings/addons';
import { useSettingsQuery } from 'src/api/queries/userQueries';
import { useAiSettingsQuery } from 'src/api/queries/aiSettingsQueries';
import { useFeatureAvailability } from 'src/hooks/useFeatureAvailability';
import { config } from 'src/utils/config';

export type AiAgentAvailability = {
  inAppAvailable: boolean;
  mcpAvailable: boolean;
  /** The saas-side AI APIs (budget/sessions) 404'd — the in-app agent is
   *  present but should render a read-only "enabling soon" state during the
   *  rollout. */
  backendUnavailable: boolean;
  isLoading: boolean;
};

/**
 * Determines whether the user can run the AI Agent inside the app
 * (chat sidebar) versus only via an external MCP client.
 *
 * In-app requires: env flag, AI_ASSISTANT addon added+enabled at the
 * account level, AI Agent feature enabled, and the current role allowed.
 * MCP is always available (any user can connect Claude/ChatGPT externally
 * with their FileSpin OAuth account).
 */
export function useAiAgentAvailable(role?: string): AiAgentAvailability {
  // Ensure addons settings are loaded for this session. Onboarding pages
  // don't go through FileSpinPickerProvider, so we load on demand here.
  const { isLoading: settingsLoading } = useSettingsQuery();

  const addonsSettings = useAtomValue(addonsSettingsAtom);
  const aiAddonAdded = !!addonsSettings?.AI_ASSISTANT?.added;
  const aiAddonEnabled = aiAddonAdded && !!addonsSettings?.AI_ASSISTANT?.enabled;

  const { data: aiSettings, isLoading: aiSettingsLoading } = useAiSettingsQuery({
    enabled: aiAddonAdded,
    retry: false,
  });

  const roleAllowed =
    !role || !aiSettings?.allowed_roles ? true : aiSettings.allowed_roles.includes(role);

  const backendUnavailable = useFeatureAvailability('ai-agent') === 'unavailable';

  const inAppAvailable =
    !!config.chatEnabled &&
    aiAddonEnabled &&
    !!aiSettings?.enabled &&
    roleAllowed &&
    !backendUnavailable;

  return {
    inAppAvailable,
    mcpAvailable: true,
    backendUnavailable,
    isLoading: settingsLoading || (aiAddonAdded && aiSettingsLoading),
  };
}
